import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useBrand } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveAsset, hideOnError } from "@/lib/assets";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboard, Ticket, CalendarCheck2, User, Sun, Moon, LogOut, MessageSquare } from "lucide-react";
import { useEffect } from "react";

const CLIENT_NAV = [
  { to: "/client/dashboard", label: "Home", icon: LayoutDashboard, testId: "cnav-home" },
  { to: "/client/tickets", label: "Support", icon: Ticket, testId: "cnav-tickets" },
  { to: "/client/chat", label: "Chat", icon: MessageSquare, testId: "cnav-chat" },
  { to: "/client/meetings", label: "Meetings", icon: CalendarCheck2, testId: "cnav-meetings" },
  { to: "/client/profile", label: "Profile", icon: User, testId: "cnav-profile" },
];

export default function ClientLayout() {
  const { user, logout, kind } = useAuth();
  const { theme, toggle } = useTheme();
  const { brand, logoSrc } = useBrand();
  const navigate = useNavigate();

  useEffect(() => {
    if (kind !== "client") navigate("/portal");
  }, [kind, navigate]);

  return (
    <div className="min-h-screen bg-background">
      {/* Premium glass header */}
      <header className="topbar-glass sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center gap-3 h-14 md:h-16 px-4">
          <Link to="/client/dashboard" className="flex items-center gap-2 min-w-0">
            {logoSrc ? (
              <img src={logoSrc} alt={brand.app_name} className="h-9 md:h-10 object-contain" onError={hideOnError} />
            ) : (
              <>
                <div className="h-9 w-9 rounded-xl flex items-center justify-center font-black text-white text-sm shrink-0"
                     style={{ background: `linear-gradient(135deg, ${brand?.primary_color || "#21095b"}, #FFA35C)` }}>
                  {(brand.app_name?.[0] || "C").toUpperCase()}
                </div>
                <span className="font-bold text-base md:text-lg tracking-tight truncate">{brand.client_portal_name || brand.app_name || "Client Portal"}</span>
              </>
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex gap-1 ml-6">
            {CLIENT_NAV.map((n) => (
              <NavLink
                key={n.to} to={n.to} data-testid={n.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-accent/10 text-accent" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1" />
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="client-theme-toggle" className="rounded-full">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="client-user-menu">
              <Avatar className="h-9 w-9 cursor-pointer ring-2 ring-transparent hover:ring-accent transition">
                <AvatarImage src={resolveAsset(user?.avatar_url)} alt={user?.name} onError={hideOnError} />
                <AvatarFallback className="bg-accent/10 text-accent font-bold">
                  {(user?.name?.[0] || "C").toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="client-logout" className="text-destructive cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 md:p-6 fade-in pb-32 md:pb-8">
        <Outlet />
      </main>

      {/* Premium floating bottom nav (mobile only) */}
      <nav className="md:hidden mobile-bottom-nav" data-testid="client-mobile-bottom-nav">
        {CLIENT_NAV.map((n) => (
          <NavLink
            key={n.to} to={n.to} data-testid={`m-${n.testId}`}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <n.icon />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
